import React, { useState, useEffect, useMemo, useCallback } from "react"
import { useForm } from "react-hook-form"

import parsePhoneNumber, { getCountryCallingCode, CountryCode } from "libphonenumber-js"
import * as Yup from "yup"

import { yupResolver } from "@hookform/resolvers/yup"
import { colors, IconErrorMedium } from "@izettle/otto"
import { useLocalization } from "@izettle/tenko-react"

import {
  Content,
  Footer,
  Button,
  LargeInput,
  Container,
  ErrorSpan,
  Form,
  LargeText,
  SvgIcon,
  Flex,
} from "@/core/components/shared"
import { useSlider } from "@/core/components/slider"
import { Phone } from "@/modules/sign-up/api/types"
import {
  TrackingAction,
  useSignUpTracking,
} from "@/modules/sign-up/hooks/tracking-sign-up"

import { CountryModalButton } from "./country-modal"

const DEFAULT_COUNTRY = "US"

const Error = ({ label }: { label: string | undefined }) => {
  return <ErrorSpan>{label}</ErrorSpan>
}

export type PhoneSlideProps = {
  purchaseUuid: string
  nextSlide: number
  confirmText?: string
  onConfirm: (name: string, value: any, next: () => void) => void
  value?: string
  placeholder?: string
}

const defaultValues = { phone: "" }

export const PhoneSlide = ({
  purchaseUuid,
  nextSlide,
  confirmText,
  onConfirm,
  value,
  placeholder,
}: PhoneSlideProps) => {
  const { text } = useLocalization()
  const { goTo, index } = useSlider()
  const [phoneCountryCode, setPhoneCountryCode] = useState<CountryCode>(DEFAULT_COUNTRY)
  const [phoneValue, setPhoneValue] = useState("")
  const { track } = useSignUpTracking(purchaseUuid)
  const [hasChange, setHasChange] = useState(false)
  const [hasRetunToEdit, setHasRetunToEdit] = useState(false)

  useEffect(() => {
    if (hasChange) {
      setHasRetunToEdit(true)
      setHasChange(false)
    }
  }, [index])

  useEffect(() => {
    if (hasChange) {
      track(hasRetunToEdit ? TrackingAction.EditedInfo : TrackingAction.EnteredInfo, {
        field: "PhoneNumber",
      })
    }
  }, [hasChange])

  const next = (nextParams?: any) => goTo(nextSlide, nextParams)

  const schema = Yup.object().shape({
    phone: Yup.string(),
  })

  const { handleSubmit, formState, setError, setValue, control } = useForm({
    defaultValues,
    resolver: yupResolver(schema),
  })

  const onSubmit = (values: { phone: string }) => {
    if (values.phone) {
      const phoneNumber = parsePhoneNumber(values.phone, phoneCountryCode)

      if (!phoneNumber?.isValid()) {
        setError("phone", { message: text("SIGNUP_VALID_PHONE_REQUIRED") })
      } else {
        const phone: Phone = {
          number: `${phoneNumber.nationalNumber}`,
          countryCode: `${phoneNumber.countryCallingCode}`,
        }
        onConfirm("phone", phone, next)
      }
    } else {
      onConfirm("phone", "", next)
    }
  }

  const setPhoneField = useCallback(
    (newValue: string) => {
      const phoneNumber = parsePhoneNumber(newValue, phoneCountryCode)
      const phoneText = phoneNumber?.formatNational() ?? newValue

      setPhoneValue(
        phoneText === newValue || phoneText[0] !== "0"
          ? phoneText
          : phoneText.substring(1, phoneText.length),
      )
      setValue("phone", phoneText)
    },
    [phoneCountryCode],
  )

  useEffect(() => {
    if (value) {
      setPhoneField(value)
    }
  }, [value])

  useEffect(() => {
    setPhoneField(phoneValue)
  }, [phoneCountryCode])

  const handlePhoneInputChange = (event: any) => {
    const newValue: string = event.target.value ?? ""

    setPhoneField(newValue)

    setHasChange(true)
  }

  const handleCountrySelect = (newCode: string) => {
    setPhoneCountryCode(newCode as CountryCode)
  }

  const labelLeftPosition = useMemo(
    () => `${1.5 + getCountryCallingCode(phoneCountryCode).length}ch`,
    [phoneCountryCode],
  )

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Container>
        <Content>
          <LargeInput
            name="phone"
            type="tel"
            placeholder={placeholder ?? text("SIGNUP_ENTER_YOUR_PHONE")}
            control={control}
            onChange={handlePhoneInputChange}
            value={phoneValue}
            ariaInvalid={!!formState.errors.phone}
            labelLeftPosition={labelLeftPosition}
            prefixElement={
              <LargeText
                style={{
                  margin: "0 8px 0 0",
                  fontWeight: 600,
                  color: colors.text.highlight,
                }}
              >
                <CountryModalButton
                  onSelect={handleCountrySelect}
                >{`+${getCountryCallingCode(phoneCountryCode)}`}</CountryModalButton>
              </LargeText>
            }
          />
          {formState.errors.phone ? (
            <Flex>
              <SvgIcon
                html={IconErrorMedium.svg}
                color={colors.text.error}
                margin="0 16px 0 0"
              />
              <Error label={formState.errors.phone?.message} />
            </Flex>
          ) : null}
        </Content>
        <Footer>
          <Button size="large" type="submit" data-testid="slide-phone-submit-button">
            {confirmText}
          </Button>
        </Footer>
      </Container>
    </Form>
  )
}
